/* eslint-disable class-methods-use-this, max-lines, max-lines-per-function */
import { EventBus, Events } from "jiffy-store";
import { formatPhoneNumber, normalizePhoneNumber } from "common/utils/format/phone";
import { getUserId, getCountryId } from "common/utils/user-context";
import { track } from "common/utils/mixpanel/tracker";
import { showTwoFactorModal } from "components/jiffy/masthead-redesign2025/local-delivery/utils/two-factor-modal-helper";
import { AddressPayloadBuilder } from "components/jiffy/masthead-redesign2025/local-delivery/utils/address-payload-builder";
import AddressApiService from "components/jiffy/masthead-redesign2025/local-delivery/address-saver/address-api-service";
import AddressFieldValidator from "components/jiffy/masthead-redesign2025/local-delivery/address-editor/address-field-validator";
import AddressServiceValidator from "components/jiffy/masthead-redesign2025/local-delivery/address-editor/address-service-validator";
import {
  getSelectorMapping,
  getFieldToSelectorMap,
  getFormFieldMapping,
  getErrorSelectors,
  ADDRESS_FIELDS
} from "./address-fields";

const SELECTORS = {
  form: ".js-address-editor-form",
  fullName: ".js-address-editor-full-name",
  address1: ".js-address-editor-address-line-1",
  address2: ".js-address-editor-address-line-2",
  zipcode: ".js-address-editor-zip-code",
  city: ".js-address-editor-city",
  phone: ".js-address-editor-phone",
  submitButton: ".js-address-editor-submit",
  cancelButton: ".js-address-editor-cancel",
  errorMessage: ".js-address-editor-error-message",
  useThisAddressButton: ".js-address-editor-use-this-address",
  verifiedIcon: ".js-address-editor-verified-icon",
  panel: ".js-slide-panel[data-panel-id='local-delivery-address-editor-slide-panel']"
};

const ERROR_CONTAINER_SELECTORS = getErrorSelectors();

const MESSAGES = {
  submitError: "An error occurred while processing your request. Please try again.",
  buttonSaving: "Saving...",
  buttonSaveAddress: "Save Address",
  consoleError: "Error submitting address form:",
  honeybadgerError: "Error submitting address form"
};

/**
 * @class AddressEditor
 *
 * Manages the address editor form, including:
 * - Populating fields with existing address data
 * - Form validation on blur events
 * - Phone number formatting
 * - Submit/cancel handlers
 * - Error display management
 * - Integration with API for saving addresses
 */
class AddressEditor {
  #addressData = null;

  #originalAddressData = null;

  #twoFactorAuthUsed = false;

  #formData = {
    fullName: "",
    address1: "",
    address2: "",
    zipcode: "",
    city: "",
    phone: "",
    stateId: null,
    stateName: "",
    customAddress: false
  };

  #errors = {
    fullName: "",
    address1: "",
    address2: "",
    zipcode: "",
    city: "",
    phone: ""
  };

  #isSubmitting = false;

  #validationFailed = false;

  /**
   * @type {AddressServiceValidator}
   */
  #addressServiceValidator = null;

  #boundHandlers = null;

  /**
   * Map of CSS selectors to field names for event delegation
   * @private
   */
  static #FIELD_SELECTOR_MAP = getSelectorMapping();

  /**
   * Map of field names to CSS selectors for form population
   * @private
   */
  static #FIELD_NAME_TO_SELECTOR = getFieldToSelectorMap();

  /**
   * Map of form field names (snake_case) to internal property names (camelCase)
   * @private
   */
  static #FORM_FIELD_TO_PROPERTY = getFormFieldMapping();

  /**
   * Initializes the address editor with the provided address data
   * and populates the form fields
   * @param {Object} addressData - The address data to populate the form with
   * @param {string} addressData.fullName - Full name (optional)
   * @param {string} addressData.address1 - Address line 1
   * @param {string} addressData.address2 - Address line 2
   * @param {string} addressData.zipcode - ZIP code
   * @param {string} addressData.city - City
   * @param {string} addressData.phone - Phone number (optional)
   * @param {number} addressData.id - Address ID (for edit mode)
   * @public
   */
  init(addressData = null) {
    this.#addressData = addressData;
    this.#originalAddressData = addressData ? { ...addressData } : null;
    this.#twoFactorAuthUsed = false;
    this.#addressServiceValidator = new AddressServiceValidator();
    this.#resetForm();
    this.#bindEventListeners();

    if (addressData) {
      this.#setFormFields();
    }
  }

  get #$panel() {
    return document.querySelector(SELECTORS.panel);
  }

  /**
   * Cleans up the address editor
   * @public
   */
  destroy() {
    this.#setLoading(false);
    this.#resetForm();
    this.#unbindEventListeners();
    this.#addressServiceValidator = null;
  }

  get #$form() {
    return document.querySelector(SELECTORS.form);
  }

  get #$inputFields() {
    return this.#$form?.querySelectorAll("input") || [];
  }

  get #$submitBtn() {
    return this.#$form?.querySelector(SELECTORS.submitButton);
  }

  get #$cancelBtn() {
    return this.#$form?.querySelector(SELECTORS.cancelButton);
  }

  get #$useThisAddressBtn() {
    return this.#$form?.querySelector(SELECTORS.useThisAddressButton);
  }

  get #$errorMessage() {
    return this.#$form?.querySelector(SELECTORS.errorMessage);
  }

  get #$phoneInput() {
    return this.#$form?.querySelector(SELECTORS.phone);
  }

  /**
   * Binds event listeners to form elements
   * @private
   */
  #bindEventListeners() {
    if (!this.#$form) return;

    this.#boundHandlers = {
      submit: this.#handleSubmit.bind(this),
      cancel: this.#handleCancel.bind(this),
      formBlur: this.#handleFormBlur.bind(this),
      formInput: this.#handleFormInput.bind(this),
      useThisAddress: this.#handleUseThisAddress.bind(this)
    };

    // Form submit
    this.#$form?.addEventListener("submit", this.#boundHandlers.submit);

    // Delegated blur event for all form fields
    this.#$form?.addEventListener("blur", this.#boundHandlers.formBlur, true);

    // Delegated input event for phone formatting
    this.#$form?.addEventListener("input", this.#boundHandlers.formInput, true);

    // Buttons
    this.#$cancelBtn?.addEventListener("click", this.#boundHandlers.cancel);
    this.#$useThisAddressBtn?.addEventListener("click", this.#boundHandlers.useThisAddress);
  }

  /**
   * Unbinds event listeners from form elements
   * @private
   */
  #unbindEventListeners() {
    if (!this.#boundHandlers) return;

    this.#$form?.removeEventListener("submit", this.#boundHandlers.submit);
    this.#$form?.removeEventListener("blur", this.#boundHandlers.formBlur, true);
    this.#$form?.removeEventListener("input", this.#boundHandlers.formInput, true);
    this.#$cancelBtn?.removeEventListener("click", this.#boundHandlers.cancel);
    this.#$useThisAddressBtn?.removeEventListener("click", this.#boundHandlers.useThisAddress);

    this.#boundHandlers = null;
  }

  /**
   * Handles delegated blur events for all form fields
   * @param {Event} event - Blur event
   * @private
   */
  #handleFormBlur(event) {
    const fieldName = this.#getFieldNameFromElement(event.target);
    if (fieldName) {
      this.#handleFieldBlur(fieldName);
    }
  }

  /**
   * Handles delegated input events for form fields
   * @param {Event} event - Input event
   * @private
   */
  #handleFormInput(event) {
    if (event.target === this.#$phoneInput) {
      this.#handlePhoneInput(event);
    }
  }

  /**
   * Gets field name from an element by matching its selector
   * @param {HTMLElement} element - The element to identify
   * @returns {string|null} Field name or null if not found
   * @private
   */
  #getFieldNameFromElement(element) {
    const entries = Object.entries(AddressEditor.#FIELD_SELECTOR_MAP);
    for (const [selector, fieldName] of entries) {
      if (element.matches(selector)) {
        return fieldName;
      }
    }
    return null;
  }

  /**
   * Handles field blur events for validation
   * @param {string} fieldName - Name of the field to validate
   * @private
   */
  #handleFieldBlur(fieldName) {
    this.#syncFormData();
    AddressFieldValidator.validateField(fieldName, this.#formData, this.#errors);
    this.#displayFieldError(fieldName);
  }

  /**
   * Handles phone input events for formatting
   * @param {Event} event - Input event
   * @private
   */
  #handlePhoneInput(event) {
    const formatted = formatPhoneNumber(event.target.value);
    // eslint-disable-next-line no-param-reassign
    event.target.value = formatted;
    this.#formData.phone = formatted;
  }

  /**
   * Handles "Use this address" button click
   * @private
   */
  #handleUseThisAddress() {
    this.#formData.customAddress = true;
    this.#validationFailed = false;
    this.#clearAllErrors();
    this.#toggleUseThisAddressButton(false);
  }

  /**
   * Handles form submission
   * @param {Event} event - Submit event
   * @private
   */
  async #handleSubmit(event) {
    event.preventDefault();

    if (this.#isSubmitting) return;

    this.#syncFormData();
    this.#clearGeneralError();

    // Validate form
    if (!AddressFieldValidator.validateForm(this.#formData, this.#errors)) {
      this.#displayAllFieldErrors();
      return;
    }

    this.#setLoading(true);

    try {
      // Validate address with API
      const validationResult = await this.#addressServiceValidator.validateAddress({
        formData: this.#formData,
        countryId: getCountryId(),
        errors: this.#errors,
        errorMessageElement: this.#$errorMessage,
        userId: getUserId()
      });

      if (!validationResult.success) {
        this.#showGeneralError(validationResult.errorMessage);
        this.#validationFailed = true;
        this.#toggleUseThisAddressButton(true);
        this.#setLoading(false);
        return;
      }

      // Check if two-factor authentication is needed
      if (validationResult.twoFactorNeeded) {
        this.#showTwoFactorModal();
        return;
      }

      // Save address
      await this.#saveAddress();
    } catch (error) {
      this.#showGeneralError(MESSAGES.submitError);
      this.#setLoading(false);

      // eslint-disable-next-line no-console
      console.error(MESSAGES.consoleError, error);

      window.Honeybadger?.notify(MESSAGES.honeybadgerError, {
        params: {
          error: JSON.stringify(error, Object.getOwnPropertyNames(error)),
          address: this.#addressData
        }
      });
    }
  }

  /**
   * Handles cancel button click
   * @param {Event} event - Click event
   * @private
   */
  #handleCancel(event) {
    event.preventDefault();
    EventBus.publish(Events.ADDRESS_EDITOR_CANCEL);
  }

  /**
   * Saves the address via API
   * @param {string|null} authToken - Optional authentication token for two-factor auth
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   * @private
   */
  async #saveAddress(authToken = null) {
    const payload = this.#buildApiPayload(authToken);
    const options = {};

    if (this.#addressData?.id) {
      options.addressId = this.#addressData.id;
    }

    const result = await AddressApiService.saveAddress(payload, options);

    if (result.success) {
      // Track address update if this is an edit (not a new address)
      if (this.#addressData?.id) {
        this.#trackAddressUpdate(result.data);
      }

      this.#resetForm();
      EventBus.publish(Events.ADDRESS_EDITOR_SUCCESS, result.data);
    } else {
      this.#showGeneralError(result.error);
    }

    return result;
  }

  /**
   * Builds API payload from form data
   * @param {string|null} authToken - Optional authentication token
   * @returns {Object} API payload
   * @private
   */
  #buildApiPayload(authToken = null) {
    return AddressPayloadBuilder.buildPayload(
      {
        address1: this.#formData.address1,
        address2: this.#formData.address2,
        zipcode: this.#formData.zipcode,
        city: this.#formData.city
      },
      {
        phone: this.#formData.phone,
        fullName: this.#formData.fullName,
        customAddress: this.#formData.customAddress,
        userId: getUserId(),
        authToken
      }
    );
  }

  /**
   * Shows the two-factor authentication modal
   * @private
   */
  #showTwoFactorModal() {
    showTwoFactorModal({
      saveAddress: authToken => {
        this.#twoFactorAuthUsed = !!authToken;
        return this.#saveAddress(authToken);
      },
      setLoading: isLoading => this.#setLoading(isLoading),
      errorMessageElement: this.#$errorMessage,
      addressId: this.#addressData?.id,
      targetContainer: this.#$panel
    });
  }

  /**
   * Populates the form fields with the address data
   * @private
   */
  #setFormFields() {
    if (!this.#addressData) {
      return;
    }

    // Populate each field using the static field mapping
    Object.entries(AddressEditor.#FIELD_NAME_TO_SELECTOR).forEach(([dataKey, selector]) => {
      const value = this.#addressData[dataKey];
      const $input = document.querySelector(selector);

      if ($input) {
        $input.value = value || "";
        this.#formData[dataKey] = value || "";
      }
    });

    // Format phone number if present
    if (this.#formData.phone && this.#$phoneInput) {
      const formatted = formatPhoneNumber(this.#formData.phone);
      this.#$phoneInput.value = formatted;
      this.#formData.phone = formatted;
    }

    // Populate state data if present
    if (this.#addressData.stateId) {
      this.#formData.stateId = this.#addressData.stateId;
    }
    if (this.#addressData.stateName) {
      this.#formData.stateName = this.#addressData.stateName;
    }
  }

  /**
   * Syncs form data from DOM inputs
   * @private
   */
  #syncFormData() {
    if (!this.#$form) return;

    const formData = new FormData(this.#$form);

    // Map form field names (snake_case) to internal property names (camelCase)
    Object.entries(AddressEditor.#FORM_FIELD_TO_PROPERTY).forEach(
      ([formFieldName, propertyName]) => {
        const value = formData.get(formFieldName);
        if (value !== null) {
          this.#formData[propertyName] = value;
        }
      }
    );
  }

  /**
   * Resets the form to initial state
   * @private
   */
  #resetForm() {
    this.#formData = {
      fullName: "",
      address1: "",
      address2: "",
      zipcode: "",
      city: "",
      phone: "",
      stateId: null,
      stateName: "",
      customAddress: false
    };

    this.#errors = {
      fullName: "",
      address1: "",
      address2: "",
      zipcode: "",
      city: "",
      phone: ""
    };

    this.#isSubmitting = false;
    this.#validationFailed = false;

    // Reset form fields
    this.#$form?.reset();

    this.#clearAllErrors();
    this.#toggleUseThisAddressButton(false);
  }

  /**
   * Displays error for a specific field
   * @param {string} fieldName - Name of the field
   * @private
   */
  #displayFieldError(fieldName) {
    const $errorContainer = this.#$form?.querySelector(ERROR_CONTAINER_SELECTORS[fieldName]);
    if (!$errorContainer) return;

    const errorMessage = this.#errors[fieldName];

    if (errorMessage) {
      $errorContainer.textContent = errorMessage;
      $errorContainer.classList.remove("hidden");
    } else {
      $errorContainer.textContent = "";
      $errorContainer.classList.add("hidden");
    }
  }

  /**
   * Displays all field errors
   * @private
   */
  #displayAllFieldErrors() {
    Object.keys(this.#errors).forEach(fieldName => {
      this.#displayFieldError(fieldName);
    });
  }

  /**
   * Clears all field errors
   * @private
   */
  #clearAllErrors() {
    Object.keys(this.#errors).forEach(fieldName => {
      this.#errors[fieldName] = "";
      this.#displayFieldError(fieldName);
    });
    this.#clearGeneralError();
  }

  /**
   * Shows general error message
   * @param {string} message - Error message
   * @private
   */
  #showGeneralError(message) {
    if (!this.#$errorMessage) return;

    this.#$errorMessage.textContent = message;
    this.#$errorMessage.classList.remove("hidden");
  }

  /**
   * Clears general error message
   * @private
   */
  #clearGeneralError() {
    if (!this.#$errorMessage) return;

    this.#$errorMessage.textContent = "";
    this.#$errorMessage.classList.add("hidden");
  }

  /**
   * Toggles visibility of "Use this address" button
   * @param {boolean} show - Whether to show (true) or hide (false) the button
   * @private
   */
  #toggleUseThisAddressButton(show) {
    if (!this.#$useThisAddressBtn) return;

    this.#$useThisAddressBtn.classList.toggle("hidden", !show);
  }

  /**
   * Disables or enables input fields
   * @param {boolean} disable - Whether to disable (true) or enable (false) the fields
   */
  #disableInputFields(disable) {
    this.#$inputFields.forEach($input => {
      // eslint-disable-next-line no-param-reassign
      $input.disabled = disable;
    });
  }

  /**
   * Toggles form disabled state
   * @param {boolean} disable - Whether to disable (true) or enable (false) the form
   * @private
   */
  #toggleFormDisabled(disable) {
    this.#$form?.classList.toggle("address-editor__form--disabled", disable);
  }

  /**
   * Gets list of fields that changed from original
   * @returns {Array<string>} Array of changed field names
   * @private
   */
  #getChangedFields() {
    if (!this.#originalAddressData) {
      return [];
    }

    // Get editable fields from centralized config (fields with selectors)
    const editableFields = Object.keys(ADDRESS_FIELDS).filter(
      fieldName => ADDRESS_FIELDS[fieldName].selector !== null
    );

    const changedFields = [];

    editableFields.forEach(field => {
      let original = (this.#originalAddressData[field] || "").trim();
      let current = (this.#formData[field] || "").trim();

      // Normalize phone numbers before comparison (remove formatting)
      if (field === "phone") {
        original = normalizePhoneNumber(original);
        current = normalizePhoneNumber(current);
      }

      if (original !== current) {
        changedFields.push(field);
      }
    });

    return changedFields;
  }

  /**
   * Determines verification status of an address
   * @param {Object} addressData - Address object
   * @returns {string} "verified" or "unverified"
   * @private
   * @static
   */
  static #getVerificationStatus(addressData) {
    return addressData?.verifiedAt ? "verified" : "unverified";
  }

  /**
   * Tracks address update completion event
   * @param {Object} updatedAddress - The updated address data from API
   * @private
   */
  #trackAddressUpdate(updatedAddress) {
    if (!this.#originalAddressData?.id) {
      return;
    }

    const previousVerificationStatus = AddressEditor.#getVerificationStatus(
      this.#originalAddressData
    );
    const newVerificationStatus = AddressEditor.#getVerificationStatus(updatedAddress);
    const fieldsChanged = this.#getChangedFields();

    const trackingData = {
      address_id: String(this.#originalAddressData.id),
      previous_verification_status: previousVerificationStatus,
      new_verification_status: newVerificationStatus,
      required_2fa: this.#twoFactorAuthUsed,
      fields_changed: fieldsChanged,
      ...(this.#twoFactorAuthUsed && { "2fa_success": true })
    };

    track("address_update_completed", trackingData);
  }

  /**
   * Sets loading state
   * @param {boolean} isLoading - Loading state
   * @private
   */
  #setLoading(isLoading) {
    this.#isSubmitting = isLoading;

    if (!this.#$submitBtn) return;

    this.#toggleFormDisabled(isLoading);
    this.#disableInputFields(isLoading);
    this.#$submitBtn.disabled = isLoading;
    this.#$submitBtn.textContent = isLoading ? MESSAGES.buttonSaving : MESSAGES.buttonSaveAddress;
  }
}

export default AddressEditor;
